import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CirclePauseIcon, CirclePlayIcon, CircleXIcon } from '@air/design-icons';
import { FileStatusButton } from '@air/feature-file-status/ui';
import {
  pausableStates,
  setUploadsResumedAction,
  UploadStatus,
  uploadByIdSelector,
  uploadProgressSelector,
} from '@air/redux-uploader';
import { useDeleteUpload, usePauseUploadById } from '@air/upload-utils';

import { usePrivateUploadDelete } from '~/components/Upload/hooks/usePrivateUploadDelete';
import { getWorkspaceUploadsItemStatusCopy } from '~/components/v2/components/workspace/workspace-uploads/getWorkspaceUploadsItemStatusCopy';
import { WorkspaceUploadsListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsListItem';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceUploadsInProgressListItemProps = {
  isOnline: boolean;
  uploadId: string;
};

export const WorkspaceUploadsInProgressListItem = memo(
  ({ isOnline, uploadId }: WorkspaceUploadsInProgressListItemProps) => {
    const dispatch = useDispatch();
    const upload = useAirSelector((state) => uploadByIdSelector(state, uploadId));
    const uploadProgress = useAirSelector((state) => uploadProgressSelector(state, uploadId));
    const { onDeleteUpload } = usePrivateUploadDelete({ analyticsContext: 'workspace_uploads' });
    const { deleteUpload } = useDeleteUpload({ onDelete: onDeleteUpload });
    const { pauseUploadById } = usePauseUploadById();

    const handlePause = useCallback(() => {
      void pauseUploadById(uploadId);
    }, [pauseUploadById, uploadId]);

    const handleResume = useCallback(() => {
      dispatch(setUploadsResumedAction({ uploadIds: [uploadId] }));
    }, [dispatch, uploadId]);

    const handleCancel = useCallback(() => {
      if (!upload) {
        return;
      }

      void deleteUpload(upload);
    }, [deleteUpload, upload]);

    if (!upload) {
      return null;
    }

    const currentBytesUploaded = uploadProgress.progress;
    const sizeInBytes = upload.apiUploadData.size || upload.file.size || 0;
    const canPause = pausableStates.includes(upload.status);
    const isPaused = upload.status === UploadStatus.paused;
    const statusCopy = getWorkspaceUploadsItemStatusCopy({
      currentBytesUploaded,
      sizeInBytes,
      status: upload.status,
    });

    const showPauseResumeToggle = (isPaused && isOnline) || (canPause && !isPaused);

    return (
      <WorkspaceUploadsListItem
        actions={
          <>
            {showPauseResumeToggle ? (
              <FileStatusButton
                appearance="ghost"
                data-testid={isPaused ? 'WORKSPACE_UPLOADS_ITEM_RESUME' : 'WORKSPACE_UPLOADS_ITEM_PAUSE'}
                label={isPaused ? 'Resume' : 'Pause'}
                onClick={isPaused ? handleResume : handlePause}
              >
                {isPaused ? <CirclePlayIcon aria-hidden size="sm" /> : <CirclePauseIcon aria-hidden size="sm" />}
              </FileStatusButton>
            ) : null}
            <FileStatusButton
              appearance="ghost"
              data-testid="WORKSPACE_UPLOADS_ITEM_CANCEL"
              label="Cancel"
              onClick={handleCancel}
            >
              <CircleXIcon aria-hidden size="sm" />
            </FileStatusButton>
          </>
        }
        ext={upload.apiUploadData.ext ?? ''}
        statusCopy={statusCopy}
        thumbnailSource={upload.imageBase64}
        title={upload.apiUploadData.importedName ?? ''}
        uploadId={upload.id}
      />
    );
  },
);

WorkspaceUploadsInProgressListItem.displayName = 'WorkspaceUploadsInProgressListItem';
